import { Middleware } from '@reduxjs/toolkit';

/**
 * Dispatch profiler for tracking down editor freezes.
 *
 * Measures Redux dispatch cost (reducer + the selector work it triggers) plus,
 * via <PerfProfiler>, React commit cost — which is a *separate* scheduler task
 * under React 18 and therefore invisible to the middleware alone.
 *
 * Available in dev and in DEVTOOLS builds (staging); compiled out of production.
 * Enable from the console, then reproduce:
 *
 *   localStorage.HOWIO_PERF = '1'; location.reload();
 *   __howioPerf.measure(3000);   // records, then prints a pasteable summary
 *   __howioPerf.copy();          // same summary, onto the clipboard
 *   localStorage.removeItem('HOWIO_PERF'); location.reload();
 */

const SLOW_ACTION_MS = 16; // one frame — anything above this can drop frames

// Live logging is off by default: `console.warn` captures a stack trace per call,
// which is expensive enough to distort the very measurement we're taking (and
// makes the console unreadable during a page load that dispatches hundreds of
// actions). Everything is still recorded — read it back with report().
let verbose = false;

/**
 * Resolved once at module load, never per call.
 *
 * This used to read `localStorage` on every invocation, and the hooks below sit
 * in hot paths — the cache-key tally runs on every *call* to a cached selector,
 * the render tally on every tile render. That was hundreds of synchronous
 * storage reads per interaction: enough to distort the numbers being collected,
 * and it shipped that cost to production.
 *
 * Every term in PROFILABLE is a build-time constant, so production bundles fold
 * this to `false` and the minifier drops the guarded bodies. Where it is included
 * it costs one boolean read. Toggling needs a reload, which is already the
 * documented flow.
 */
// Dev, plus any build made with DEVTOOLS on — currently .env.staging, matching the
// redux-devtools gate in configureStore. Read as raw `import.meta.env` rather than
// through utils/env so Vite can fold it to a literal and tree-shake this file out of
// the production bundle entirely.
const PROFILABLE: boolean =
  import.meta.env.DEV || import.meta.env.VITE_DEVTOOLS === 'true' || import.meta.env.REACT_APP_DEVTOOLS === 'true';

const ENABLED: boolean =
  PROFILABLE && typeof window !== 'undefined' && window.localStorage?.getItem('HOWIO_PERF') === '1';

export const perfEnabled = (): boolean => ENABLED;

interface ActionStat {
  type: string;
  count: number;
  totalMs: number;
  maxMs: number;
}

const stats = new Map<string, ActionStat>();
const slowLog: { type: string; ms: number }[] = [];

/**
 * Catches the same action being dispatched twice in quick succession with an
 * identical payload — the signature of duplicated work (a component mounted
 * twice, or two copies of a saga watching the same action). Records the call
 * site of the second dispatch, which is what you need to find the culprit.
 */
const DUPE_WINDOW_MS = 1000;
const dupes = new Map<string, { type: string; count: number; stack: string }>();
let lastAction: { key: string; at: number } | null = null;

function checkDuplicate(action: any) {
  let payload: string;
  try {
    payload = JSON.stringify(action.payload) ?? '';
  } catch {
    return; // circular payload — can't compare, skip
  }

  const key = `${action.type}|${payload}`;
  const now = performance.now();
  const previous = lastAction;
  lastAction = { key, at: now };

  if (!previous || previous.key !== key || now - previous.at > DUPE_WINDOW_MS) return;

  const entry = dupes.get(action.type) ?? {
    type: action.type,
    count: 0,
    // Trim the middleware frames so the first line is the actual dispatcher.
    stack: (new Error().stack ?? '').split('\n').slice(3, 9).join('\n'),
  };
  entry.count++;
  dupes.set(action.type, entry);
}

export const perfMiddleware: Middleware = () => next => (action: any) => {
  if (!ENABLED || !action?.type) return next(action);

  checkDuplicate(action);

  const start = performance.now();
  const result = next(action);
  const ms = performance.now() - start;

  const stat = stats.get(action.type) ?? { type: action.type, count: 0, totalMs: 0, maxMs: 0 };
  stat.count++;
  stat.totalMs += ms;
  stat.maxMs = Math.max(stat.maxMs, ms);
  stats.set(action.type, stat);

  if (ms > SLOW_ACTION_MS) {
    slowLog.push({ type: action.type, ms });
    // console.log rather than warn/error — no stack capture, so the logging
    // itself stays cheap enough not to skew the numbers.
    if (verbose) console.log(`[perf] ${action.type} ${ms.toFixed(1)}ms`);
  }

  return result;
};

/**
 * Counts recomputes of an expensive selector. Selectors that rebuild on every
 * entity change are the usual cause of a slow editor, and a recompute count is
 * far easier to read than a flame chart. Wrap the *result function*:
 *
 *   createSelector([a, b], countRecomputes('moduleNodes', (a, b) => …))
 */
export function countRecomputes<F extends (...args: any[]) => any>(name: string, fn: F): F {
  if (!ENABLED) return fn;

  return ((...args: any[]) => {
    const start = performance.now();
    const value = fn(...args);
    recordRecompute(name, performance.now() - start);
    return value;
  }) as F;
}

/**
 * Same idea as `countRecomputes`, but wraps the body rather than the function —
 * use it where wrapping the result function would break `createSelector`'s
 * contextual typing of its parameters.
 */
export function timeRecompute<T>(name: string, fn: () => T): T {
  if (!ENABLED) return fn();

  const start = performance.now();
  const value = fn();
  recordRecompute(name, performance.now() - start);
  return value;
}

const recomputes = new Map<string, { name: string; count: number; totalMs: number }>();

function recordRecompute(name: string, ms: number) {
  const entry = recomputes.get(name) ?? { name, count: 0, totalMs: 0 };
  entry.count++;
  entry.totalMs += ms;
  recomputes.set(name, entry);
}

/**
 * Tallies the cache keys a re-reselect `createCachedSelector` is called with.
 * A selector that never recomputes but keeps changing identity is alternating
 * between cache entries — this shows how many distinct keys are in play and how
 * often each is hit, which names the culprit directly.
 */
const cacheKeys = new Map<string, Map<string, number>>();

export function noteCacheKey<K>(name: string, key: K): K {
  if (!ENABLED) return key;

  const keys = cacheKeys.get(name) ?? new Map<string, number>();
  const label = String(key);
  keys.set(label, (keys.get(label) ?? 0) + 1);
  cacheKeys.set(name, keys);
  return key;
}

/** Count a recompute without timing it — for bodies with early returns. */
export function noteRecompute(name: string) {
  if (ENABLED) recordRecompute(name, 0);
}

/**
 * Attributes each recompute of a memoised selector to the input that lost
 * reference identity. A high recompute count means some input is handing back a
 * fresh object every call; this says which one instead of leaving you to guess.
 *
 * Call it at the top of the selector's result function with the same inputs, in
 * order, that the enclosing createSelector was given.
 */
const lastInputs = new Map<string, unknown[]>();
const inputBlame = new Map<string, Map<number, number>>();
const inputNames = new Map<string, string[]>();

export function blameInputs(name: string, names: string[], values: unknown[]) {
  if (!ENABLED) return;

  inputNames.set(name, names);
  const previous = lastInputs.get(name);
  lastInputs.set(name, values);
  if (!previous) return;

  const blame = inputBlame.get(name) ?? new Map<number, number>();
  values.forEach((value, i) => {
    if (previous[i] !== value) blame.set(i, (blame.get(i) ?? 0) + 1);
  });
  inputBlame.set(name, blame);
}

/**
 * React commit costs, fed by <PerfProfiler>. This is the layer the Redux
 * middleware cannot see: with React 18, a store update schedules a render in a
 * separate scheduler task (the one Chrome reports as a slow 'message' handler),
 * so it lands well after `dispatch` has returned.
 */
interface CommitStat {
  id: string;
  mounts: number;
  updates: number;
  totalMs: number;
  maxMs: number;
}

const commits = new Map<string, CommitStat>();

export function recordCommit(id: string, phase: 'mount' | 'update' | 'nested-update', actualDuration: number) {
  if (!ENABLED) return;

  const entry = commits.get(id) ?? { id, mounts: 0, updates: 0, totalMs: 0, maxMs: 0 };
  if (phase === 'mount') entry.mounts++;
  else entry.updates++;
  entry.totalMs += actualDuration;
  entry.maxMs = Math.max(entry.maxMs, actualDuration);
  commits.set(id, entry);
}

/**
 * Per-component render tally. A commit total tells you the subtree is slow;
 * this tells you whether that's one expensive component or hundreds of cheap
 * ones re-rendering when they shouldn't (i.e. a broken React.memo).
 */
const renders = new Map<string, number>();

export function recordRender(name: string) {
  if (!ENABLED) return;
  renders.set(name, (renders.get(name) ?? 0) + 1);
}

function reset() {
  stats.clear();
  recomputes.clear();
  slowLog.length = 0;
  inputBlame.clear();
  lastInputs.clear();
  commits.clear();
  renders.clear();
  cacheKeys.clear();
  dupes.clear();
  lastAction = null;
}

function report() {
  const actions = [...stats.values()].sort((a, b) => b.totalMs - a.totalMs);
  console.group('[perf] dispatch cost by action (worst total first)');
  console.table(
    actions.map(s => ({
      type: s.type,
      count: s.count,
      totalMs: +s.totalMs.toFixed(1),
      avgMs: +(s.totalMs / s.count).toFixed(1),
      maxMs: +s.maxMs.toFixed(1),
    })),
  );
  if (recomputes.size) {
    console.log('selector recomputes');
    console.table(
      [...recomputes.values()]
        .sort((a, b) => b.totalMs - a.totalMs)
        .map(r => ({ name: r.name, count: r.count, totalMs: +r.totalMs.toFixed(1) })),
    );
  }
  if (slowLog.length) {
    // The ordered timeline matters as much as the totals: it shows whether one
    // interaction fans out into a cascade of dispatches.
    console.log(`${slowLog.length} dispatches over ${SLOW_ACTION_MS}ms, in order`);
    console.table(slowLog.map(s => ({ type: s.type, ms: +s.ms.toFixed(1) })));
  }
  console.groupEnd();
  return { actions, recomputes: [...recomputes.values()], slowLog };
}

/**
 * Plain-text summary. `console.table` can't be copied out of devtools, and the
 * raw console is unusable for sharing — this produces something pasteable.
 */
function text(): string {
  const lines: string[] = [];
  const actions = [...stats.values()].sort((a, b) => b.totalMs - a.totalMs);
  const grandTotal = actions.reduce((sum, s) => sum + s.totalMs, 0);

  lines.push(`== dispatch cost (${grandTotal.toFixed(0)}ms total across ${actions.length} action types) ==`);
  lines.push('totalMs  count  avgMs  maxMs  type');
  for (const s of actions.slice(0, 25)) {
    lines.push(
      [
        s.totalMs.toFixed(1).padStart(7),
        String(s.count).padStart(6),
        (s.totalMs / s.count).toFixed(1).padStart(6),
        s.maxMs.toFixed(1).padStart(6),
        `  ${s.type}`,
      ].join(''),
    );
  }

  if (recomputes.size) {
    lines.push('', '== selector recomputes ==');
    for (const r of [...recomputes.values()].sort((a, b) => b.totalMs - a.totalMs)) {
      lines.push(`${r.totalMs.toFixed(1).padStart(7)}  x${r.count}  ${r.name}`);
      const blame = inputBlame.get(r.name);
      const names = inputNames.get(r.name) ?? [];
      if (blame)
        for (const [i, n] of [...blame.entries()].sort((a, b) => b[1] - a[1]))
          lines.push(`             ↳ input "${names[i] ?? i}" changed identity x${n}`);
    }
  }

  if (dupes.size) {
    const total = [...dupes.values()].reduce((sum, d) => sum + d.count, 0);
    lines.push('', `== duplicate dispatches (${total} total, identical payload back-to-back) ==`);
    for (const d of [...dupes.values()].sort((a, b) => b.count - a.count)) {
      lines.push(`  x${d.count}  ${d.type}`);
      for (const frame of d.stack.split('\n')) lines.push(`        ${frame.trim()}`);
    }
  }

  if (cacheKeys.size) {
    lines.push('', '== cached-selector keys (>1 key means cache thrashing) ==');
    for (const [name, keys] of cacheKeys) {
      lines.push(`${name}: ${keys.size} distinct key(s)`);
      for (const [key, count] of [...keys.entries()].sort((a, b) => b[1] - a[1]))
        lines.push(`             ↳ "${key}" x${count}`);
    }
  }

  if (commits.size) {
    const totalCommit = [...commits.values()].reduce((sum, c) => sum + c.totalMs, 0);
    lines.push('', `== React commits (${totalCommit.toFixed(0)}ms total) ==`);
    lines.push('totalMs  maxMs  mounts  updates  subtree');
    for (const c of [...commits.values()].sort((a, b) => b.totalMs - a.totalMs))
      lines.push(
        [
          c.totalMs.toFixed(1).padStart(7),
          c.maxMs.toFixed(1).padStart(7),
          String(c.mounts).padStart(8),
          String(c.updates).padStart(9),
          `  ${c.id}`,
        ].join(''),
      );
  }

  if (renders.size) {
    lines.push('', '== component renders ==');
    for (const [name, count] of [...renders.entries()].sort((a, b) => b[1] - a[1]))
      lines.push(`${String(count).padStart(7)}  ${name}`);
  }

  if (slowLog.length) {
    lines.push('', `== ${slowLog.length} dispatches over ${SLOW_ACTION_MS}ms, in order ==`);
    for (const s of slowLog.slice(0, 60)) lines.push(`${s.ms.toFixed(1).padStart(7)}  ${s.type}`);
  }

  return lines.join('\n');
}

/** Print the pasteable summary and put it on the clipboard. */
function copy() {
  const out = text();
  console.log(out);
  (window as any).copy?.(out);
  return out;
}

/**
 * Scope a recording to a single interaction. Call it, do the thing, and the
 * report prints itself — avoids the reset/report pair straddling the click and
 * picking up unrelated background dispatches.
 *
 *   __howioPerf.measure(3000);  // then click the tile
 */
function measure(ms = 3000) {
  reset();
  console.log(`[perf] recording for ${ms}ms — go`);
  setTimeout(() => {
    console.log('[perf] recording finished');
    console.log(text());
  }, ms);
}

// Gated on DEV as well as the runtime flag: this assignment is a module-level
// side effect, so without a build-time constant here Rollup must assume the
// reporting functions are reachable and keeps all of them in the bundle.
if (PROFILABLE && typeof window !== 'undefined') {
  (window as any).__howioPerf = {
    reset,
    report,
    measure,
    text,
    copy,
    enabled: perfEnabled,
    verbose: (on = true) => {
      verbose = on;
    },
  };
}
